import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Archive as ArchiveIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext';
import { createContextAwareApiClient } from '../api/contextAwareApiClient';

/**
 * ArchiveView provides telemetry data archival operations
 */
function ArchiveView() {
  const { 
    customerContext, 
    getContextData,
    validateContext 
  } = useAppContext();
  
  // Use ref to store the service to prevent infinite loops
  const fleetServiceRef = useRef();
  fleetServiceRef.current = createContextAwareApiClient(getContextData, validateContext).fleetService;
  
  const [selectedFleet, setSelectedFleet] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [archiveStats, setArchiveStats] = useState(null);
  const [showStatsDialog, setShowStatsDialog] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);

  // Available Fleets (populated from API)
  const [availableFleets, setAvailableFleets] = useState([]);
  const [fleetsLoading, setFleetsLoading] = useState(false);
  const [fleetsError, setFleetsError] = useState(null);

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchFleets = useCallback(async (customerId, retryAttempt = 0) => {
    if (!customerId) {
      return;
    }

    setFleetsLoading(true);
    setFleetsError(null);
    
    try {
      const response = await fleetServiceRef.current.getFleetsByCustomerId(customerId);
      
      if (response.success && response.data) {
        const fleetData = response.data.data || [];
        setAvailableFleets(fleetData);
        
        // Auto-select the first fleet if none is selected
        if (!selectedFleet && fleetData.length > 0) {
          setSelectedFleet(fleetData[0].fleetId);
        }
        
        setFleetsError(null);
      } else {
        setFleetsError(`Failed to load fleets: ${response.message || 'Unknown error'}`);
        setAvailableFleets([]);
      }
    } catch (err) {
      // Retry logic for network errors
      if (err.message === 'Network Error' && retryAttempt < 2) {
        setTimeout(() => {
          fetchFleets(customerId, retryAttempt + 1);
        }, 1000);
        return;
      }
      
      setFleetsError(`Error: ${err.message}`);
      setAvailableFleets([]);
    } finally {
      setFleetsLoading(false);
    }
  }, [selectedFleet]);

  // Fetch fleets when the customer context changes
  useEffect(() => {
    if (!customerContext) {
      setAvailableFleets([]);
      setSelectedFleet('');
      setFleetsError(null);
      return;
    }
    
    // Only fetch if we have a valid customer ID
    if (customerContext.customerId) {
      fetchFleets(customerContext.customerId);
    }
  }, [customerContext, fetchFleets]);

  const handleGetStats = async () => {
    if (!selectedFleet) {
      setMessage('Please select a fleet');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const params = new URLSearchParams({
        fleetId: selectedFleet
      });
      
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await fetch(
        `https://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/archive/stats?${params}`
      );

      const result = await response.json();

      if (result.status === 'success') {
        setArchiveStats(result.data);
        setShowStatsDialog(true);
      } else {
        setMessage(result.message || 'Failed to get archive statistics');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to get archive statistics: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!selectedFleet) {
      setMessage('Please select a fleet');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const exportData = {
        fleetId: selectedFleet,
        startDate: startDate || null,
        endDate: endDate || null,
        includeMetadata: true
      };

      console.log('ArchiveView Export request data:', exportData);

      const response = await fetch(
        'https://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/archive/export',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(exportData)
        }
      );

      const result = await response.json();
      console.log('ArchiveView Export response:', result);
      console.log('ArchiveView Export response data:', JSON.stringify(result.data, null, 2));

      if (result.status === 'success') {
        setExportResult(result.data);
        setShowExportDialog(true);
        setMessage('Archive export completed successfully!');
        setMessageType('success');
      } else {
        setMessage(result.message || 'Export failed');
        setMessageType('error');
      }
    } catch (error) {
      console.error('ArchiveView Export error:', error);
      setMessage('Export failed: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handlePurge = async () => {
    if (!selectedFleet) {
      setMessage('Please select a fleet');
      setMessageType('error');
      return;
    }

    if (!confirmPurge) {
      setMessage('Please confirm purge operation');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const purgeData = {
        fleetId: selectedFleet,
        startDate: startDate || null,
        endDate: endDate || null,
        confirmPurge: true
      };

      const response = await fetch(
        'https://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/archive/purge',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(purgeData)
        }
      );

      const result = await response.json();

      if (result.status === 'success') {
        setMessage(`Purge completed: ${result.data.records_deleted} records deleted`);
        setMessageType('success');
        setConfirmPurge(false);
      } else {
        setMessage(result.message || 'Purge failed');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Purge failed: ' + error.message);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (exportId, filename) => {
    const downloadUrl = `https://sabalsql-dbam-ji2csljmda-uk.a.run.app/api/archive/download/${exportId}/${filename}`;
    window.open(downloadUrl, '_blank');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Telemetry Archive Manager
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Export, import, and manage telemetry data archives on a fleet basis
      </Typography>

      {message && (
        <Alert severity={messageType} sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {/* Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Archive Configuration</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Fleet</InputLabel>
                <Select
                  value={selectedFleet}
                  onChange={(e) => setSelectedFleet(e.target.value)}
                  label="Fleet"
                  disabled={fleetsLoading || !customerContext || availableFleets.length === 0}
                >
                  {fleetsLoading ? (
                    <MenuItem value="">
                      <CircularProgress size={20} />
                      <Typography sx={{ ml: 1 }}>Loading Fleets...</Typography>
                    </MenuItem>
                  ) : !customerContext ? (
                    <MenuItem value="" disabled>Select a customer first</MenuItem>
                  ) : fleetsError ? (
                    <MenuItem value="" disabled>Error loading fleets</MenuItem>
                  ) : availableFleets.length === 0 ? (
                    <MenuItem value="" disabled>No fleets found</MenuItem>
                  ) : (
                    availableFleets.map((fleet) => (
                      <MenuItem key={fleet.fleetId} value={fleet.fleetId}>
                        {fleet.fleetDescription || fleet.fleetName || fleet.fleetId}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {!customerContext && (
                <Typography color="warning.main" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Please select a customer first.
                </Typography>
              )}
              {fleetsError && (
                <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                  {fleetsError}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Operations */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>Archive Operations</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <InfoIcon />}
                onClick={handleGetStats}
                disabled={loading || !selectedFleet}
                size="large"
              >
                {loading ? 'Loading...' : 'Get Stats'}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} /> : <ArchiveIcon />}
                onClick={handleExport}
                disabled={loading || !selectedFleet}
                size="large"
              >
                {loading ? 'Exporting...' : 'Export Archive'}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={loading ? <CircularProgress size={20} /> : <DeleteIcon />}
                onClick={handlePurge}
                disabled={loading || !selectedFleet}
                size="large"
              >
                {loading ? 'Purging...' : 'Purge Data'}
              </Button>
            </Grid>
          </Grid>
          
          {/* Purge Confirmation */}
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Confirm Purge</InputLabel>
              <Select
                value={confirmPurge}
                onChange={(e) => setConfirmPurge(e.target.value)}
                label="Confirm Purge"
              >
                <MenuItem value={false}>No (Cancel)</MenuItem>
                <MenuItem value={true}>Yes (Proceed with Purge)</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Dialog */}
      <Dialog
        open={showStatsDialog}
        onClose={() => setShowStatsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Archive Statistics</DialogTitle>
        <DialogContent>
          {archiveStats && (
            <Box>
              <Typography variant="h6" gutterBottom>Summary</Typography>
              <List>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Total Records" 
                    secondary={(archiveStats.summary?.total_records || 0).toLocaleString()} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Canopy Count" 
                    secondary={archiveStats.summary?.canopy_count || 0} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Data Size" 
                    secondary={`${archiveStats.summary?.size_mb || 0} MB`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Date Range" 
                    secondary={
                      archiveStats.summary?.earliest_record && archiveStats.summary?.latest_record 
                        ? `${new Date(archiveStats.summary.earliest_record).toLocaleDateString()} - ${new Date(archiveStats.summary.latest_record).toLocaleDateString()}`
                        : 'No data available'
                    } 
                  />
                </ListItem>
              </List>

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Per-Canopy Breakdown</Typography>
              <List>
                {(archiveStats.canopies || []).map((canopy) => (
                  <ListItem key={canopy.canopy_id}>
                    <ListItemIcon><CheckIcon /></ListItemIcon>
                    <ListItemText 
                      primary={`Canopy ${canopy.canopy_id}`}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            {(canopy.record_count || 0).toLocaleString()} records
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {canopy.earliest_record && canopy.latest_record 
                              ? `${new Date(canopy.earliest_record).toLocaleDateString()} - ${new Date(canopy.latest_record).toLocaleDateString()}`
                              : 'No date range available'
                            }
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStatsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Result Dialog */}
      <Dialog
        open={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Export Completed</DialogTitle>
        <DialogContent>
          {exportResult && (
            <Box>
              <Typography variant="h6" gutterBottom>Export Details</Typography>
              <List>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Export ID" 
                    secondary={exportResult.archive_id} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="File Size" 
                    secondary={`${((exportResult.file_size || 0) / 1024 / 1024).toFixed(2)} MB`} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Record Count" 
                    secondary={(exportResult.record_count || 0).toLocaleString()} 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><InfoIcon /></ListItemIcon>
                  <ListItemText 
                    primary="Canopy Count" 
                    secondary={exportResult.canopy_count || 0} 
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExportDialog(false)}>Close</Button>
          {exportResult && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => handleDownload(exportResult.archive_id, exportResult.filename)}
            >
              Download Archive
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ArchiveView; 